export const SUPPORTED_LOCALES = ['en', 'zh-CN', 'yue-Hant-HK'] as const;
export type AppLocale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: AppLocale = 'en';
export const URL_LANG_PARAM = 'lang';
export const LOCALE_STORAGE_KEY = 'gift.locale';

export const LOCALE_LABELS: Record<AppLocale, string> = {
  en: 'English',
  'zh-CN': '简体中文',
  'yue-Hant-HK': '廣東話',
};

const LOCALE_ALIASES: Record<string, AppLocale> = {
  en: 'en',
  'en-us': 'en',
  'en-gb': 'en',
  zh: 'zh-CN',
  'zh-cn': 'zh-CN',
  'zh-hans': 'zh-CN',
  'zh-hans-cn': 'zh-CN',
  yue: 'yue-Hant-HK',
  'yue-hk': 'yue-Hant-HK',
  'yue-hant': 'yue-Hant-HK',
  'yue-hant-hk': 'yue-Hant-HK',
  'zh-hk': 'yue-Hant-HK',
  'zh-mo': 'yue-Hant-HK',
  'zh-hant': 'yue-Hant-HK',
  'zh-hant-hk': 'yue-Hant-HK',
};

function canonicalize(value: string): string {
  return String(value || '').trim().replace(/_/g, '-').toLowerCase();
}

export function isSupportedLocale(value: string | null | undefined): value is AppLocale {
  return SUPPORTED_LOCALES.includes(value as AppLocale);
}

export function normalizeLocale(value: string | null | undefined): AppLocale | null {
  if (!value) return null;
  if (isSupportedLocale(value)) return value;
  const canonical = canonicalize(value);
  if (canonical in LOCALE_ALIASES) return LOCALE_ALIASES[canonical];

  const exact = SUPPORTED_LOCALES.find((locale) => canonicalize(locale) === canonical);
  if (exact) return exact;

  const base = canonical.split('-')[0];
  if (base && base in LOCALE_ALIASES) return LOCALE_ALIASES[base];
  return null;
}

export function getLocaleLabel(locale: AppLocale): string {
  return LOCALE_LABELS[locale];
}

export function resolveLocaleFromUrl(search: string): AppLocale | null {
  const params = new URLSearchParams(search);
  return normalizeLocale(params.get(URL_LANG_PARAM));
}

export function readStoredLocale(storage: Pick<Storage, 'getItem'> | null | undefined): AppLocale | null {
  try {
    return normalizeLocale(storage?.getItem(LOCALE_STORAGE_KEY) || '');
  } catch {
    return null;
  }
}

export function writeStoredLocale(storage: Pick<Storage, 'setItem'> | null | undefined, locale: AppLocale): void {
  try {
    storage?.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Ignore storage failures such as private mode restrictions.
  }
}

export function resolveBrowserLocale(languages: readonly string[] | null | undefined): AppLocale | null {
  if (!Array.isArray(languages)) return null;
  for (const language of languages) {
    const normalized = normalizeLocale(language);
    if (normalized) return normalized;
  }
  return null;
}

export function resolvePreferredLocale(input: {
  search?: string;
  storage?: Pick<Storage, 'getItem'> | null;
  languages?: readonly string[] | null;
  fallback?: AppLocale;
}): AppLocale {
  return (
    resolveLocaleFromUrl(input.search || '')
    || readStoredLocale(input.storage)
    || resolveBrowserLocale(input.languages)
    || input.fallback
    || DEFAULT_LOCALE
  );
}

export function updateSearchWithLocale(
  search: string,
  locale: AppLocale,
  options?: { keepDefault?: boolean },
): string {
  const params = new URLSearchParams(search);
  if (locale === DEFAULT_LOCALE && !options?.keepDefault) {
    params.delete(URL_LANG_PARAM);
  } else {
    params.set(URL_LANG_PARAM, locale);
  }
  const next = params.toString();
  return next ? `?${next}` : '';
}

export function stripLocaleFromSearch(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(URL_LANG_PARAM);
  const next = params.toString();
  return next ? `?${next}` : '';
}
